import React, { useState } from 'react';
import api from '../services/api';
import { Search, X, CheckCircle, AlertCircle, Loader, AlertTriangle, Edit3 } from 'lucide-react';

const COR = {
  laranja:  '#F07020',
  azul:     '#1E6DB5',
  ciano:    '#29B6D8',
  verde:    '#22C55E',
  amarelo:  '#F5A623',
  vermelho: '#EF4444',
  bg:       '#0A0F1A',
  card:     '#0E1826',
  card2:    '#121f30',
  borda:    '#1A2740',
  texto:    '#F1F5F9',
  sub:      '#64748B',
};

const ORIGENS = ['URA', 'Discador', 'Manual'];

const formatarCNPJ = v => {
  const d = v.replace(/\D/g,'');
  if (d.length <= 2)  return d;
  if (d.length <= 5)  return `${d.slice(0,2)}.${d.slice(2)}`;
  if (d.length <= 8)  return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8)}`;
  return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8,12)}-${d.slice(12,14)}`;
};

// ── MODAL INDICAÇÃO ──────────────────────────────────────────────────────────
export function ModalIndicacao({ onClose, onSuccess }) {
  const [cnpj, setCnpj]               = useState('');
  const [buscando, setBuscando]       = useState(false);
  const [dadosCNPJ, setDadosCNPJ]     = useState(null);
  const [erroCNPJ, setErroCNPJ]       = useState('');
  const [modoManual, setModoManual]   = useState(false);
  const [formManual, setFormManual]   = useState({ razao_social:'', cidade:'', estado:'' });
  const [form, setForm]               = useState({ nome_completo:'', telefone:'', email:'', ramo_atividade:'', origem:'URA' });
  const [salvando, setSalvando]       = useState(false);
  const [sucesso, setSucesso]         = useState(false);
  const [erro, setErro]               = useState('');
  const [duplicidade, setDuplicidade] = useState(null);
  const [enviandoDup, setEnviandoDup] = useState(false);
  const [sucessoDup, setSucessoDup]   = useState(false);

  const buscarCNPJ = async () => {
    const digits = cnpj.replace(/\D/g,'');
    if (digits.length !== 14) { setErroCNPJ('CNPJ deve ter 14 dígitos'); return; }
    setBuscando(true); setErroCNPJ(''); setDuplicidade(null); setDadosCNPJ(null); setModoManual(false);
    try {
      const dupRes = await api.get(`/registros/indicacao/verificar/${digits}`);
      if (dupRes.data.indicado) {
        setDuplicidade(dupRes.data.indicacao);
        api.get(`/registros/cnpj/${digits}`).then(r => setDadosCNPJ(r.data)).catch(() => {});
        setBuscando(false);
        return;
      }
      const res = await api.get(`/registros/cnpj/${digits}`);
      setDadosCNPJ(res.data);
      setForm(f => ({ ...f, email: res.data.email || '', ramo_atividade: res.data.ramo || '', nome_completo: res.data.nome_socio || '' }));
    } catch (e) {
      // Receita não encontrou — ativar modo manual
      setErroCNPJ('CNPJ não encontrado na Receita Federal. Preencha os dados manualmente abaixo.');
      setModoManual(true);
    }
    setBuscando(false);
  };

  const enviarParaSupervisor = async () => {
    setEnviandoDup(true);
    try {
      const digits = cnpj.replace(/\D/g,'');
      let dr = dadosCNPJ;
      if (!dr) {
        const res = await api.get(`/registros/cnpj/${digits}`).catch(() => null);
        dr = res?.data || null;
      }
      await api.post('/registros/duplicidade', {
        tipo: 'indicacao', cnpj: digits,
        razao_social:   dr?.razao_social   || duplicidade?.razao_social   || '',
        cidade:         dr?.cidade         || duplicidade?.cidade         || '',
        estado:         dr?.estado         || duplicidade?.estado         || '',
        nome_completo:  form.nome_completo || dr?.nome_socio || '',
        telefone:       form.telefone      || '',
        email:          form.email         || dr?.email || '',
        ramo_atividade: form.ramo_atividade|| dr?.ramo  || '',
        origem:         form.origem,
        id_existente:   duplicidade?.id_indicacao || null,
      });
      setSucessoDup(true);
    } catch (e) {
      setErro(e.response?.data?.error || 'Erro ao enviar para supervisor');
    }
    setEnviandoDup(false);
  };

  // Dados finais — da Receita ou do preenchimento manual
  const dadosFinais = dadosCNPJ || (modoManual ? {
    cnpj:           cnpj.replace(/\D/g,''),
    cnpj_formatado: cnpj,
    razao_social:   formManual.razao_social,
    cidade:         formManual.cidade,
    estado:         formManual.estado,
  } : null);

  const manualValido = !modoManual || (formManual.razao_social.trim() && formManual.cidade.trim() && formManual.estado.trim());
  const podeRegistrar = !!dadosFinais && manualValido;

  const salvar = async () => {
    if (!dadosFinais) { setErro('Busque o CNPJ primeiro'); return; }
    if (modoManual) {
      if (!formManual.razao_social.trim()) { setErro('Razão Social é obrigatória'); return; }
      if (!formManual.cidade.trim())       { setErro('Cidade é obrigatória'); return; }
      if (!formManual.estado.trim())       { setErro('Estado é obrigatório'); return; }
    }
    if (!form.telefone) { setErro('Telefone obrigatório'); return; }
    if (!/^[1-9]{2}9[0-9]{8}$/.test(form.telefone)) { setErro('Telefone inválido. Use: DDD + 9 dígitos. Ex: 11999999999'); return; }
    if (!form.origem) { setErro('Selecione a origem'); return; }
    setSalvando(true); setErro('');
    try {
      await api.post('/registros/indicacao', {
        cnpj:         dadosFinais.cnpj,
        razao_social: dadosFinais.razao_social,
        cidade:       dadosFinais.cidade,
        estado:       dadosFinais.estado,
        ...form,
      });
      setSucesso(true);
      setTimeout(() => { onSuccess?.(); onClose(); }, 2000);
    } catch (e) {
      setErro(e.response?.data?.error || 'Erro ao registrar indicação');
    }
    setSalvando(false);
  };

  return (
    <Overlay onClose={onClose}>
      <div style={s.modal}>
        <ModalHeader titulo="Registrar Indicação" cor={COR.amarelo} onClose={onClose}/>
        {sucesso ? (
          <SucessoBox mensagem="Indicação registrada com sucesso!"/>
        ) : sucessoDup ? (
          <SucessoBox mensagem="Enviado ao supervisor para análise!" cor={COR.azul}/>
        ) : duplicidade ? (
          <DuplicidadeLayout tipo="indicacao" registroExistente={duplicidade} form={form} setForm={setForm}
            erro={erro} enviando={enviandoDup} onCancelar={onClose} onEnviar={enviarParaSupervisor}/>
        ) : (
          <>
            <div style={s.grupo}>
              <label style={s.label}>CNPJ *</label>
              <div style={{display:'flex',gap:8}}>
                <input style={s.input} placeholder="00.000.000/0000-00" maxLength={18}
                  value={cnpj}
                  onChange={e => { setCnpj(formatarCNPJ(e.target.value)); setModoManual(false); setErroCNPJ(''); setDadosCNPJ(null); }}
                  onKeyDown={e => e.key === 'Enter' && buscarCNPJ()}/>
                <BtnBuscar onClick={buscarCNPJ} loading={buscando}/>
              </div>
              {erroCNPJ && (
                <p style={{...s.erroInline, color: modoManual ? COR.amarelo : COR.vermelho, marginTop:6}}>
                  {erroCNPJ}
                </p>
              )}
            </div>

            {/* Dados da Receita */}
            {dadosCNPJ && <CardReceita dados={dadosCNPJ}/>}

            {/* Modo manual */}
            {modoManual && (
              <div style={{background:`${COR.amarelo}10`, border:`1px solid ${COR.amarelo}44`, borderRadius:10, padding:'14px', marginBottom:16}}>
                <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:12}}>
                  <Edit3 size={13} color={COR.amarelo}/>
                  <span style={{color:COR.amarelo,fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:0.5}}>
                    Preenchimento manual
                  </span>
                </div>
                <Campo label="Razão Social *" value={formManual.razao_social}
                  onChange={v => setFormManual(f=>({...f,razao_social:v}))} placeholder="Nome da empresa"/>
                <div style={s.grid2}>
                  <Campo label="Cidade *" value={formManual.cidade}
                    onChange={v => setFormManual(f=>({...f,cidade:v}))} placeholder="Ex: São Paulo"/>
                  <Campo label="Estado *" value={formManual.estado}
                    onChange={v => setFormManual(f=>({...f,estado:v.toUpperCase().slice(0,2)}))} placeholder="Ex: SP"/>
                </div>
              </div>
            )}

            {/* Campos comuns — aparecem com Receita ou manual */}
            {(dadosCNPJ || modoManual) && (
              <>
                <div style={s.grid2}>
                  <Campo label="Nome do contato" value={form.nome_completo} onChange={v => setForm(f=>({...f,nome_completo:v}))} placeholder="Nome completo"/>
                  <Campo label="Telefone *"       value={form.telefone}      onChange={v => setForm(f=>({...f,telefone:v.replace(/\D/g,'')}))} placeholder="Ex: 11999999999"/>
                </div>
                <div style={s.grid2}>
                  <Campo label="E-mail" value={form.email} onChange={v => setForm(f=>({...f,email:v}))} placeholder="email@empresa.com"/>
                  <CampoSelect label="Origem *" value={form.origem} onChange={v => setForm(f=>({...f,origem:v}))} opcoes={ORIGENS}/>
                </div>
                <Campo label="Ramo de atividade" value={form.ramo_atividade} onChange={v => setForm(f=>({...f,ramo_atividade:v}))} placeholder="Ramo da empresa"/>
              </>
            )}

            {erro && <ErroBox mensagem={erro}/>}

            <div style={s.rodape}>
              <button style={s.btnCancelar} onClick={onClose}>Cancelar</button>
              <button
                style={{...s.btnSalvar,background:`linear-gradient(135deg,${COR.amarelo},#c4821a)`,opacity:salvando||!podeRegistrar?0.6:1}}
                onClick={salvar} disabled={salvando||!podeRegistrar}>
                {salvando ? <Loader size={14} style={{animation:'spin 1s linear infinite'}}/> : '✓'}
                {salvando ? 'Salvando...' : 'Registrar Indicação'}
              </button>
            </div>
          </>
        )}
      </div>
    </Overlay>
  );
}

// ── MODAL VENDA ──────────────────────────────────────────────────────────────
export function ModalVenda({ onClose, onSuccess }) {
  const [cnpj, setCnpj]               = useState('');
  const [verificando, setVerif]       = useState(false);
  const [indicacao, setIndicacao]     = useState(null);
  const [erroCNPJ, setErroCNPJ]       = useState('');
  const [origem, setOrigem]           = useState('URA');
  const [salvando, setSalvando]       = useState(false);
  const [sucesso, setSucesso]         = useState(false);
  const [erro, setErro]               = useState('');
  const [dupVenda, setDupVenda]       = useState(null);
  const [formDup, setFormDup]         = useState({ nome_completo:'', telefone:'', email:'', ramo_atividade:'', origem:'URA' });
  const [enviandoDup, setEnviandoDup] = useState(false);
  const [sucessoDup, setSucessoDup]   = useState(false);

  const verificarCNPJ = async () => {
    const digits = cnpj.replace(/\D/g,'');
    if (digits.length !== 14) { setErroCNPJ('CNPJ deve ter 14 dígitos'); return; }
    setVerif(true); setErroCNPJ(''); setIndicacao(null); setDupVenda(null);
    try {
      const res = await api.get(`/registros/indicacao/verificar/${digits}`);
      if (!res.data.indicado) {
        setErroCNPJ('⚠️ Este CNPJ não possui indicação prévia. Registre a indicação primeiro.');
        setVerif(false); return;
      }
      setIndicacao(res.data.indicacao);
      const resVenda = await api.get(`/registros/venda/verificar/${digits}`);
      if (resVenda.data.vendido) {
        setDupVenda(resVenda.data.venda);
        setFormDup(f => ({
          ...f,
          nome_completo:  res.data.indicacao.nome_completo  || '',
          email:          res.data.indicacao.email          || '',
          ramo_atividade: res.data.indicacao.ramo_atividade || '',
        }));
      }
    } catch (e) {
      setErroCNPJ(e.response?.data?.error || 'Erro ao verificar CNPJ');
    }
    setVerif(false);
  };

  const salvar = async () => {
    if (!indicacao) return;
    setSalvando(true); setErro('');
    try {
      await api.post('/registros/venda', { cnpj: indicacao.cnpj, origem });
      setSucesso(true);
      setTimeout(() => { onSuccess?.(); onClose(); }, 2000);
    } catch (e) {
      if (e.response?.status === 409 && e.response.data?.duplicado) {
        setDupVenda(e.response.data.venda_existente);
      } else {
        setErro(e.response?.data?.error || 'Erro ao registrar venda');
      }
    }
    setSalvando(false);
  };

  const enviarDupParaSupervisor = async () => {
    setEnviandoDup(true);
    const digits = cnpj.replace(/\D/g,'');
    try {
      await api.post('/registros/duplicidade', {
        tipo: 'venda', cnpj: digits,
        razao_social:   indicacao?.razao_social    || dupVenda?.razao_social   || '',
        cidade:         indicacao?.cidade          || dupVenda?.cidade         || '',
        estado:         indicacao?.estado          || dupVenda?.estado         || '',
        nome_completo:  formDup.nome_completo      || indicacao?.nome_completo || '',
        telefone:       formDup.telefone           || '',
        email:          formDup.email              || indicacao?.email         || '',
        ramo_atividade: formDup.ramo_atividade     || indicacao?.ramo_atividade|| '',
        origem:         formDup.origem,
        id_existente:   dupVenda?.id_venda         || null,
      });
      setSucessoDup(true);
    } catch (e) {
      setErro(e.response?.data?.error || 'Erro ao enviar para supervisor');
    }
    setEnviandoDup(false);
  };

  return (
    <Overlay onClose={onClose}>
      <div style={s.modal}>
        <ModalHeader titulo="Registrar Venda" cor={COR.verde} onClose={onClose}/>
        {sucesso ? (
          <SucessoBox mensagem="Venda registrada com sucesso!" cor={COR.verde}/>
        ) : sucessoDup ? (
          <SucessoBox mensagem="Enviado ao supervisor para análise!" cor={COR.azul}/>
        ) : dupVenda ? (
          <DuplicidadeLayout tipo="venda" registroExistente={dupVenda} form={formDup} setForm={setFormDup}
            erro={erro} enviando={enviandoDup} onCancelar={onClose} onEnviar={enviarDupParaSupervisor}/>
        ) : (
          <>
            <div style={{...s.alertBox,borderColor:COR.azul+'44',background:COR.azul+'10',marginBottom:20}}>
              <p style={{color:'#94a3b8',fontSize:12,margin:0,lineHeight:1.6}}>
                <span style={{color:COR.ciano,fontWeight:700}}>ℹ️ Importante:</span> Para registrar uma venda, o CNPJ deve ter sido previamente indicado. Os dados serão preenchidos automaticamente.
              </p>
            </div>
            <div style={s.grupo}>
              <label style={s.label}>CNPJ da venda *</label>
              <div style={{display:'flex',gap:8}}>
                <input style={s.input} placeholder="00.000.000/0000-00" maxLength={18}
                  value={cnpj}
                  onChange={e => { setCnpj(formatarCNPJ(e.target.value)); setIndicacao(null); setErroCNPJ(''); }}
                  onKeyDown={e => e.key === 'Enter' && verificarCNPJ()}/>
                <BtnBuscar onClick={verificarCNPJ} loading={verificando} label="Verificar"/>
              </div>
              {erroCNPJ && <p style={{...s.erroInline,color:erroCNPJ.startsWith('⚠️')?COR.amarelo:COR.vermelho}}>{erroCNPJ}</p>}
            </div>
            {indicacao && (
              <div style={s.cardReceita}>
                <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:10}}>
                  <CheckCircle size={14} color={COR.verde}/>
                  <span style={{color:COR.verde,fontSize:11,fontWeight:700}}>Indicação encontrada!</span>
                </div>
                <p style={s.receitaNome}>{indicacao.razao_social}</p>
                <p style={s.receitaSub}>{indicacao.cidade} · {indicacao.estado}</p>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginTop:10}}>
                  {[['Contato',indicacao.nome_completo],['Telefone',indicacao.telefone1],['E-mail',indicacao.email],['Ramo',indicacao.ramo_atividade]].map(([k,v]) => v ? (
                    <div key={k}><span style={{color:COR.sub,fontSize:10,display:'block'}}>{k}</span><span style={{color:COR.texto,fontSize:11}}>{v}</span></div>
                  ) : null)}
                </div>
              </div>
            )}
            {indicacao && <CampoSelect label="Origem da venda *" value={origem} onChange={setOrigem} opcoes={ORIGENS}/>}
            {erro && <ErroBox mensagem={erro}/>}
            <div style={s.rodape}>
              <button style={s.btnCancelar} onClick={onClose}>Cancelar</button>
              <button style={{...s.btnSalvar,background:`linear-gradient(135deg,${COR.verde},#16a34a)`,opacity:(!indicacao||salvando)?0.5:1}}
                onClick={salvar} disabled={!indicacao||salvando}>
                {salvando ? <Loader size={14} style={{animation:'spin 1s linear infinite'}}/> : '✓'}
                {salvando ? 'Salvando...' : 'Registrar Venda'}
              </button>
            </div>
          </>
        )}
      </div>
    </Overlay>
  );
}

// ── DUPLICIDADE ──────────────────────────────────────────────────────────────
function DuplicidadeLayout({ tipo, registroExistente, form, setForm, erro, enviando, onCancelar, onEnviar }) {
  const isVenda = tipo === 'venda';
  const cor = isVenda ? COR.verde : COR.amarelo;
  return (
    <div>
      <div style={{...s.alertBox,borderColor:cor+'55',background:cor+'10',marginBottom:16}}>
        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
          <AlertTriangle size={18} color={cor}/>
          <span style={{color:cor,fontWeight:700,fontSize:14}}>CNPJ já {isVenda?'registrado':'indicado'} anteriormente</span>
        </div>
        <p style={{color:'#94a3b8',fontSize:12,margin:0,lineHeight:1.6}}>
          Este CNPJ já existe no banco de {isVenda?'venda':'indicação'}s. Você pode cancelar ou enviar para o supervisor analisar.
        </p>
      </div>
      <div style={{marginBottom:16}}>
        <p style={{color:COR.sub,fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:0.5,marginBottom:8}}>
          Registro existente ({isVenda?'venda':'indicação'}):
        </p>
        <div style={{background:'#080f1a',border:`1px solid ${COR.borda}`,borderRadius:10,padding:'12px 14px'}}>
          <p style={{color:COR.texto,fontSize:13,fontWeight:700,margin:'0 0 4px 0'}}>{registroExistente.razao_social}</p>
          <p style={{color:COR.sub,fontSize:11,margin:'0 0 8px 0'}}>{registroExistente.cidade} · {registroExistente.estado} · {registroExistente.cnpj}</p>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6}}>
            {[[isVenda?'Registrado por':'Indicado por',registroExistente.vendedor],['Origem',registroExistente.origem],['Contato',registroExistente.nome_completo],['Telefone',registroExistente.telefone1]].map(([k,v]) => v ? (
              <div key={k}><span style={{color:COR.sub,fontSize:10,display:'block'}}>{k}</span><span style={{color:'#94a3b8',fontSize:11}}>{v}</span></div>
            ) : null)}
          </div>
        </div>
      </div>
      <div style={{marginBottom:16}}>
        <p style={{color:COR.sub,fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:0.5,marginBottom:8}}>Seus dados (enviados ao supervisor):</p>
        <div style={s.grid2}>
          <Campo label="Nome do contato" value={form.nome_completo} onChange={v=>setForm(f=>({...f,nome_completo:v}))} placeholder="Nome completo"/>
          <Campo label="Telefone" value={form.telefone} onChange={v=>setForm(f=>({...f,telefone:v.replace(/\D/g,'')}))} placeholder="Ex: 11999999999"/>
        </div>
        <CampoSelect label="Origem" value={form.origem} onChange={v=>setForm(f=>({...f,origem:v}))} opcoes={ORIGENS}/>
      </div>
      {erro && <ErroBox mensagem={erro}/>}
      <div style={s.rodape}>
        <button style={s.btnCancelar} onClick={onCancelar}>Cancelar</button>
        <button style={{...s.btnSalvar,background:`linear-gradient(135deg,${COR.azul},#144E8A)`,opacity:enviando?0.7:1}} onClick={onEnviar} disabled={enviando}>
          {enviando?<Loader size={14} style={{animation:'spin 1s linear infinite'}}/>:'📋'}
          {enviando?'Enviando...':'Enviar para Supervisor'}
        </button>
      </div>
    </div>
  );
}

function Overlay({children,onClose}){return(<div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={e=>e.target===e.currentTarget&&onClose()}>{children}</div>);}
function ModalHeader({titulo,cor,onClose}){return(<div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20,paddingBottom:14,borderBottom:`1px solid ${COR.borda}`}}><div style={{display:'flex',alignItems:'center',gap:10}}><div style={{width:4,height:20,background:cor,borderRadius:2}}/><h2 style={{color:COR.texto,fontSize:16,fontWeight:700,margin:0}}>{titulo}</h2></div><button style={{background:'transparent',border:'none',color:COR.sub,cursor:'pointer',padding:4}} onClick={onClose}><X size={18}/></button></div>);}
function SucessoBox({mensagem,cor=COR.verde}){return(<div style={{display:'flex',flexDirection:'column',alignItems:'center',padding:'32px 0',gap:14}}><CheckCircle size={48} color={cor}/><p style={{color:cor,fontSize:16,fontWeight:700,margin:0,textAlign:'center'}}>{mensagem}</p></div>);}
function ErroBox({mensagem}){return(<div style={{display:'flex',alignItems:'center',gap:8,background:'#ef444415',border:'1px solid #ef444444',borderRadius:8,padding:'10px 14px',marginBottom:12}}><AlertCircle size={14} color={COR.vermelho}/><span style={{color:COR.vermelho,fontSize:12}}>{mensagem}</span></div>);}
function CardReceita({dados}){return(<div style={s.cardReceita}><div style={{display:'flex',alignItems:'center',gap:6,marginBottom:8}}><CheckCircle size={14} color={COR.verde}/><span style={{color:COR.verde,fontSize:11,fontWeight:700}}>CNPJ encontrado na Receita Federal</span></div><p style={s.receitaNome}>{dados.razao_social}</p><p style={s.receitaSub}>{dados.cidade} · {dados.estado} · {dados.cnpj_formatado}</p>{dados.nome_socio&&<p style={{...s.receitaSub,color:'#94a3b8'}}>👤 Sócio: {dados.nome_socio}</p>}{dados.ramo&&<p style={s.receitaRamo}>{dados.ramo}</p>}</div>);}
function BtnBuscar({onClick,loading,label='Buscar'}){return(<button style={{...s.btnBuscar,opacity:loading?0.7:1}} onClick={onClick} disabled={loading}>{loading?<Loader size={14} style={{animation:'spin 1s linear infinite'}}/>:<Search size={14}/>}{label}</button>);}
function Campo({label,value,onChange,placeholder}){return(<div style={s.grupo}><label style={s.label}>{label}</label><input style={s.input} placeholder={placeholder} value={value} onChange={e=>onChange(e.target.value)}/></div>);}
function CampoSelect({label,value,onChange,opcoes}){return(<div style={s.grupo}><label style={s.label}>{label}</label><select style={s.input} value={value} onChange={e=>onChange(e.target.value)}>{opcoes.map(o=><option key={o} value={o}>{o}</option>)}</select></div>);}

const s={
  modal:{background:COR.card,border:`1px solid ${COR.borda}`,borderRadius:16,padding:24,width:'100%',maxWidth:520,maxHeight:'90vh',overflowY:'auto',fontFamily:'sans-serif'},
  grupo:{marginBottom:14},
  label:{color:COR.sub,fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:0.5,display:'block',marginBottom:5},
  input:{width:'100%',background:COR.card2,border:`1px solid ${COR.borda}`,borderRadius:8,padding:'9px 12px',color:COR.texto,fontSize:13,outline:'none',boxSizing:'border-box'},
  grid2:{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12},
  btnBuscar:{display:'flex',alignItems:'center',gap:6,background:`linear-gradient(135deg,#1E6DB5,#144E8A)`,border:'none',borderRadius:8,padding:'9px 16px',color:'#fff',cursor:'pointer',fontSize:12,fontWeight:600,whiteSpace:'nowrap'},
  cardReceita:{background:'#0d1c2e',border:`1px solid ${COR.verde}33`,borderRadius:10,padding:'12px 14px',marginBottom:16},
  alertBox:{borderRadius:10,padding:'12px 14px',border:'1px solid'},
  receitaNome:{color:COR.texto,fontSize:14,fontWeight:700,margin:'0 0 4px 0'},
  receitaSub:{color:COR.sub,fontSize:12,margin:'0 0 4px 0'},
  receitaRamo:{color:'#94a3b8',fontSize:11,margin:0,fontStyle:'italic'},
  rodape:{display:'flex',gap:10,justifyContent:'flex-end',marginTop:20,paddingTop:16,borderTop:`1px solid ${COR.borda}`},
  btnCancelar:{background:'transparent',border:`1px solid ${COR.borda}`,borderRadius:8,padding:'9px 18px',color:COR.sub,cursor:'pointer',fontSize:13},
  btnSalvar:{display:'flex',alignItems:'center',gap:6,border:'none',borderRadius:8,padding:'9px 18px',color:'#fff',cursor:'pointer',fontSize:13,fontWeight:600},
  erroInline:{color:COR.vermelho,fontSize:11,margin:'5px 0 0 0'},
};
